import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MUTABLE_NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
  type NotificationPreferences,
} from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { describeError, LoadingState } from "./States";

/** Notification preferences, "download my data", and account deletion — the profile page's
 *  account-control section. */
export function AccountSettings() {
  const queryClient = useQueryClient();
  const { signOut } = useAuthPrincipal();

  const { data: prefs, isLoading } = useQuery({
    queryKey: queryKeys.notificationPreferences,
    queryFn: ({ signal }) => apiClient.getNotificationPreferences(signal),
  });

  const prefsMutation = useMutation({
    mutationFn: (body: Partial<NotificationPreferences>) =>
      apiClient.updateNotificationPreferences(body),
    onSuccess: (updated) => queryClient.setQueryData(queryKeys.notificationPreferences, updated),
  });

  const [exportError, setExportError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  function toggleType(type: (typeof MUTABLE_NOTIFICATION_TYPES)[number], enabled: boolean) {
    if (!prefs) return;
    const muted = new Set(prefs.mutedNotificationTypes);
    if (enabled) muted.delete(type);
    else muted.add(type);
    prefsMutation.mutate({ mutedNotificationTypes: [...muted] });
  }

  async function handleExport() {
    setExportError(null);
    try {
      const data = await apiClient.exportAccountData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "marketonbase-my-data.json";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(describeError(err));
    }
  }

  async function handleDelete() {
    setDeleteError(null);
    setDeleting(true);
    try {
      await apiClient.deleteAccount();
      await signOut();
    } catch (err) {
      setDeleteError(describeError(err));
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="card stack">
        <h2>Notifications</h2>
        <p className="text-sm muted">
          Order, dispute, transfer-deadline and support notifications are always on — they can
          involve your money or a deadline.
        </p>
        {isLoading && <LoadingState label="Loading preferences…" />}
        {prefs && (
          <>
            <label className="row">
              <input
                type="checkbox"
                checked={prefs.emailAlertsEnabled}
                disabled={prefsMutation.isPending}
                onChange={(e) => prefsMutation.mutate({ emailAlertsEnabled: e.target.checked })}
              />
              <span>Email me about price drops and saved-search matches</span>
            </label>
            {MUTABLE_NOTIFICATION_TYPES.map((type) => (
              <label key={type} className="row">
                <input
                  type="checkbox"
                  checked={!prefs.mutedNotificationTypes.includes(type)}
                  disabled={prefsMutation.isPending}
                  onChange={(e) => toggleType(type, e.target.checked)}
                />
                <span>In-app: {NOTIFICATION_TYPE_LABELS[type]}</span>
              </label>
            ))}
          </>
        )}
        {prefsMutation.isError && (
          <p className="field-error-text" role="alert">
            {describeError(prefsMutation.error)}
          </p>
        )}
      </div>

      <div className="card stack">
        <h2>Your data</h2>
        <p className="text-sm muted">
          Download a copy of what MarketOnBase holds about your account: profile, listings, orders,
          offers, watchlist and saved searches. Passwords, license keys and delivered files are
          not included.
        </p>
        <div>
          <button type="button" className="btn btn-secondary" onClick={() => void handleExport()}>
            Download my data
          </button>
        </div>
        {exportError && (
          <p className="field-error-text" role="alert">
            {exportError}
          </p>
        )}
      </div>

      <div className="card stack">
        <h2>Delete account</h2>
        <p className="text-sm muted">
          Erases your name, email, contact details, watchlist and notifications, and archives your
          listings. Completed orders stay on record for the other party and on-chain, without your
          details. Your wallet and any funds in it are yours and aren&apos;t touched. Not possible
          while you have an order in progress.
        </p>
        <label className="stack">
          <span className="text-sm">Type DELETE to confirm</span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
        </label>
        <div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={confirmText !== "DELETE" || deleting}
            onClick={() => void handleDelete()}
          >
            {deleting ? "Deleting…" : "Delete my account"}
          </button>
        </div>
        {deleteError && (
          <p className="field-error-text" role="alert">
            {deleteError}
          </p>
        )}
      </div>
    </>
  );
}
